import React from 'react';
import { Award, Target, Eye, Heart, Users, TrendingUp } from 'lucide-react';
import { testimonials } from '../mockData';
import ScrollReveal from '../components/ScrollReveal';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality Excellence',
      description: 'We never compromise on quality. Every piece is crafted with precision and care.'
    },
    {
      icon: Heart,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We go the extra mile to ensure you love your workspace.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We stay ahead of trends, bringing you the latest in office furniture design and functionality.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We work closely with you to understand your needs and deliver customized solutions.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Gradient */}
      <section className="relative py-24 bg-gradient-to-br from-[#222] via-[#2a2a2a] to-[#1a1a1a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #C1A57B 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-10 right-10 w-72 h-72 bg-[#C1A57B]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#C1A57B]/5 rounded-full blur-3xl animate-float-delayed"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl font-bold mb-4">About AARTEMA</h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#C1A57B] to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Transforming workspaces with premium furniture since 2009. We believe that great furniture creates great workplaces.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal direction="right">
              <div>
                <h2 className="text-5xl font-bold text-[#222] mb-6">Our Story</h2>
                <div className="w-20 h-1.5 bg-[#C1A57B] mb-6"></div>
                <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                  <p>
                    AARTEMA Office Furniture was founded with a simple vision: to create workspaces that inspire productivity and creativity. What started as a small workshop has grown into one of India's leading office furniture manufacturers.
                  </p>
                  <p>
                    With over 15 years of experience, we have furnished thousands of offices across the country, from startups to Fortune 500 companies. Our commitment to quality, design, and customer satisfaction has made us a trusted partner for businesses of all sizes.
                  </p>
                  <p>
                    Today, we continue to push the boundaries of office furniture design, combining traditional craftsmanship with modern aesthetics to create pieces that stand the test of time.
                  </p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal direction="left">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzY1MzA5NjE2fDA&ixlib=rb-4.1.0&q=85"
                    alt="AARTEMA office"
                    className="rounded-2xl w-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#C1A57B]/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-[#C1A57B]/20 to-transparent rounded-2xl -z-10 blur-2xl"></div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-24 bg-gradient-to-b from-[#EDEDED] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal delay={0}>
              <div className="group relative bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#C1A57B]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C1A57B] to-[#B09568] rounded-xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <Target className="text-white" size={32} />
                  </div>
                  <h3 className="text-3xl font-bold text-[#222] mb-4">Our Mission</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To revolutionize workspaces by delivering innovative, ergonomic, and aesthetically pleasing office furniture that enhances productivity and well-being. We strive to be the preferred choice for businesses seeking quality and design excellence.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="group relative bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#C1A57B]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C1A57B] to-[#B09568] rounded-xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <Eye className="text-white" size={32} />
                  </div>
                  <h3 className="text-3xl font-bold text-[#222] mb-4">Our Vision</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To be India's most trusted and innovative office furniture brand, setting new standards in design, sustainability, and customer service. We envision creating workspaces that inspire creativity, foster collaboration, and drive success.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-[#222] mb-4">Our Core Values</h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#C1A57B] to-transparent mx-auto"></div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="group text-center p-8 rounded-2xl hover:bg-gradient-to-br hover:from-[#EDEDED] hover:to-white transition-all duration-500 transform hover:-translate-y-3 hover:shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C1A57B] to-[#B09568] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg">
                    <value.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#222] mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-[#222] via-[#2a2a2a] to-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #C1A57B 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-4">What Our Clients Say</h2>
              <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-[#C1A57B] to-transparent mx-auto"></div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={testimonial.id} delay={index * 100}>
                <div className="group relative bg-white/5 backdrop-blur-sm p-8 rounded-2xl hover:bg-white/10 transition-all duration-500 transform hover:-translate-y-2 border border-white/10 hover:border-[#C1A57B]/50">
                  {/* Quote decoration */}
                  <div className="absolute top-6 right-6 text-6xl text-[#C1A57B]/20 font-serif">&ldquo;</div>
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-[#C1A57B] transform group-hover:scale-110 transition-transform duration-300"
                        style={{ transitionDelay: `${i * 50}ms` }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic leading-relaxed relative z-10">&ldquo;{testimonial.content}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-white text-lg">{testimonial.name}</p>
                    <p className="text-sm text-[#C1A57B]">{testimonial.position}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#C1A57B] via-[#D4B896] to-[#C1A57B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 animate-shimmer"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollReveal>
            <h2 className="text-4xl font-bold mb-4">Join Our Growing Family of Satisfied Clients</h2>
            <p className="text-xl mb-8">Experience the AARTEMA difference in your workspace</p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-white text-[#222] font-bold rounded-xl hover:bg-[#EDEDED] transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              Get In Touch
            </a>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default About;